/**
 * 表单选择器组件
 * 用于在BPMN属性面板中选择关联表单
 * 包含左侧分类树和右侧表单列表
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Table,
  Input,
  Button,
  Tree,
  Spin,
  message,
  Tag
} from 'antd';
import {
  SearchOutlined,
  FormOutlined,
  DesktopOutlined,
  MobileOutlined
} from '@ant-design/icons';
import useRequest from '@/utils/useRequest';
import useWscTableList from '@/component/UseWscTableList';

const { Search } = Input;

// 分类树转换函数
const listToTree = (items, parentId = null) => {
  // 动态确定根节点的parentId
  if (parentId === null) {
    // 找到所有可能的父节点ID
    const allParentIds = [...new Set(items.map(item => item.Pid))];
    // 找到不在子节点ID列表中的父节点ID，即为根节点
    const allChildIds = items.map(item => item.Id);
    const rootParentIds = allParentIds.filter(pid => !allChildIds.includes(pid));
    // 使用第一个根节点ID作为起始点
    parentId = rootParentIds[0] || "-1";
  }
  
  const result = [];
    for (const item of items) {
      if (item.Pid === parentId) {
        const children = listToTree(items, item.Id);
        if (children.length > 0) {
          item.children = children;
        }
        result.push(item);
      }
    }
    return result;
  };

// 分类树组件
const CategoryTreeSelector = ({ onChange }) => {
  const { request } = useRequest();
  const [gData, setData] = useState([]);
  const [selectedKeys, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const Refresh = async () => {
    try {
      setLoading(true);
      let result = await request("/api/v2/private/category/all", "POST", "json", {});
      
      if (result.success) {
        let tree = listToTree(result.data, "-1");
        setData(tree);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    Refresh();
  }, []);

  return (
    <Spin spinning={loading}>
      <div className="h-full border-r border-gray-200">
        <div className="p-2 border-b border-gray-200 bg-gray-50">
          <span className="text-sm font-medium">表单分类</span>
        </div>
        <div className="p-2 h-[calc(100%-40px)] overflow-y-auto">
          {gData.length === 0 ? (
            <div className="text-center text-gray-500 py-4">加载中...</div>
          ) : (
            <Tree
              fieldNames={{ title: 'Name', key: 'Id', children: 'children' }}
              defaultExpandAll={true}
              selectedKeys={selectedKeys}
              blockNode
              multiple={false}
              onSelect={(selectedKeys, info) => {
                setSelected(selectedKeys);
                if (onChange && selectedKeys.length > 0) {
                  onChange(selectedKeys[0], info.node.Left, info.node.Right);
                } else if (onChange && selectedKeys.length === 0) {
                  onChange(null);
                }
              }}
              treeData={gData}
            />
          )}
        </div>
      </div>
    </Spin>
  );
};

// 表单列表组件
const FormListSelector = ({ categoryFilter, onSelect, selectedFormKey, multiple = false, selectedFormKeys = [], selectedForms = [] }) => {
  const initParams = {
    pagination: {
      current: 1,
      pageSize: 10
    },
    filters: categoryFilter ? { Category: categoryFilter } : {},
    sorter: null
  };

  const { list, isLoading, pagination, setPage, setFilter, filters } = useWscTableList({
    url: "/api/v2/private/dynamicform/list",
    initParams: initParams
  });

  // 当分类过滤条件变化时更新过滤器
  useEffect(() => {
    if (categoryFilter) {
      setFilter({ ...filters, Category: categoryFilter });
    } else {
      const newFilters = { ...filters };
      delete newFilters.Category;
      setFilter(newFilters);
    }
  }, [categoryFilter]);

  const columns = [
    {
      title: '表单名称',
      dataIndex: 'Title',
      key: 'Title',
      render: (text, record) => (
        <div className="flex items-center space-x-2">
          <FormOutlined className="text-blue-500" />
          <span>{text}</span>
        </div>
      )
    },
    {
      title: '表单Key',
      dataIndex: 'Id',
      key: 'Id',
      render: (text) => (
        <code className="bg-gray-100 px-2 py-1 rounded text-sm">{text}</code>
      )
    },
    {
      title: '应用类别',
      dataIndex: 'AppCategory',
      key: 'AppCategory',
      render: (appType) => {
        if (appType === "PC" || appType === "pc") {
          return (
            <Tag icon={<DesktopOutlined />} color="blue">
              PC
            </Tag>
          );
        } else if (appType === "Mini" || appType === "MINI" || appType === "小程序") {
          return (
            <Tag icon={<MobileOutlined />} color="green">
              小程序
            </Tag>
          );
        }
        return appType;
      }
    }
  ];

  // 如果不是多选模式，添加操作列
  if (!multiple) {
    columns.push({
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => onSelect(record)}
          disabled={selectedFormKey === record.Id}
        >
          {selectedFormKey === record.Id ? '已选择' : '选择'}
        </Button>
      )
    });
  }

  // 多选模式的行选择配置
  const rowSelection = multiple ? {
    type: 'checkbox',
    selectedRowKeys: selectedFormKeys.filter(key => list.some(item => item.Id === key)),
    onSelect: (record, selected) => {
        console.log('选择行:', record, '选中状态:', selected);
        // 使用传递的selectedForms来维护跨页面的选择状态
        let newSelectedForms;
        if (selected) {
          // 添加选中的表单（避免重复）
          const exists = selectedForms.some(form => form.Id === record.Id);
          newSelectedForms = exists ? selectedForms : [...selectedForms, record];
        } else {
          // 移除取消选中的表单
          newSelectedForms = selectedForms.filter(form => form.Id !== record.Id);
        }
        
        console.log('新的选中表单列表:', newSelectedForms);
        onSelect(newSelectedForms);
      },
    onSelectAll: (selected, selectedRows, changeRows) => {
       console.log('全选/取消全选:', selected, '选中行:', selectedRows, '变化行:', changeRows);
       let newSelectedForms;
       if (selected) {
         // 全选当前页：添加当前页面所有未选中的行到已选中列表
         const currentPageIds = list.map(item => item.Id);
         const otherPageSelected = selectedForms.filter(form => !currentPageIds.includes(form.Id));
         newSelectedForms = [...otherPageSelected, ...list];
       } else {
         // 取消全选当前页：从已选中列表中移除当前页面的所有行
         const currentPageIds = list.map(item => item.Id);
         newSelectedForms = selectedForms.filter(form => !currentPageIds.includes(form.Id));
       }
       
       console.log('全选/取消全选后的表单列表:', newSelectedForms);
       onSelect(newSelectedForms);
     }
  } : undefined;

  // 处理表格变化（分页、排序、筛选）
  const handleTableChange = (pagination, filters, sorter) => {
    console.log('表格变化:', { pagination, filters, sorter });
    // 在多选模式下，分页切换时需要保持已选中的状态
    if (multiple && selectedFormKeys.length > 0) {
      console.log('分页切换前已选中的表单:', selectedFormKeys);
    }
    setPage(pagination.current);
  };

  const handleSearch = (value) => {
    setFilter({ ...filters, keywords: value });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">表单列表</span>
          <Search
            placeholder="搜索表单"
            onSearch={handleSearch}
            style={{ width: 200 }}
            size="small"
          />
        </div>
      </div>
      <div className="flex-1 p-2">
        <Table
          rowKey="Id"
          loading={isLoading}
          dataSource={list}
          columns={columns}
          rowSelection={rowSelection}
          pagination={{
            ...pagination,
            size: 'small',
            showSizeChanger: false,
            showQuickJumper: false
          }}
          onChange={handleTableChange}
          size="small"
          scroll={{ y: 300 }}
          onRow={multiple ? (record) => ({
            onClick: () => {
              const isSelected = selectedFormKeys.includes(record.Id);
              let newSelectedForms;
              if (isSelected) {
                // 从selectedForms中移除当前记录
                newSelectedForms = selectedForms.filter(form => form.Id !== record.Id);
              } else {
                // 添加当前记录到selectedForms（避免重复）
                const exists = selectedForms.some(form => form.Id === record.Id);
                newSelectedForms = exists ? selectedForms : [...selectedForms, record];
              }
              console.log('行点击选择:', record, '新的选中表单:', newSelectedForms);
              onSelect(newSelectedForms);
            }
          }) : undefined}
        />
      </div>
    </div>
  );
};

// 主表单选择器组件
const FormSelector = ({ visible, onCancel, onSelect, selectedFormKey, multiple = false, defaultSelectedForms = [] }) => {
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedForms, setSelectedForms] = useState([]);
  
  // 初始化选中的表单
  useEffect(() => {
    if (multiple && defaultSelectedForms.length > 0) {
      setSelectedForms(defaultSelectedForms);
    }
  }, [multiple, defaultSelectedForms]);

  const handleCategoryChange = (categoryId, left, right) => {
    if (categoryId) {
      setCategoryFilter({ id: categoryId, left, right });
    } else {
      setCategoryFilter(null);
    }
  };

  const handleFormSelect = (forms) => {
    if (multiple) {
      if (Array.isArray(forms)) {
        // 处理多选模式下的表单选择
        console.log('更新选中的表单:', forms);
        setSelectedForms(forms);
      } else {
        // 单个表单的选择/取消选择
        const formArray = forms ? [forms] : [];
        setSelectedForms(formArray);
      }
    } else {
      setSelectedForm(forms);
    }
  };

  const handleConfirm = () => {
    if (multiple) {
      if (selectedForms.length > 0) {
        onSelect(selectedForms);
        setSelectedForms([]);
      } else {
        message.warning('请至少选择一个表单');
      }
    } else {
      if (selectedForm) {
        onSelect(selectedForm);
        setSelectedForm(null);
      } else {
        message.warning('请选择一个表单');
      }
    }
  };

  // 清除所有选择
  const handleClearAll = () => {
    if (multiple) {
      setSelectedForms([]);
    } else {
      setSelectedForm(null);
    }
    console.log('清除所有选择');
  };

  // 重置到默认选择
  const handleReset = () => {
    if (multiple) {
      setSelectedForms(defaultSelectedForms);
    } else {
      setSelectedForm(null);
    }
    console.log('重置到默认选择');
  };

  const handleCancel = () => {
    if (multiple) {
      setSelectedForms([]);
    } else {
      setSelectedForm(null);
    }
    onCancel();
  };

  const getModalTitle = () => {
    if (multiple) {
      return `选择关联表单（多选）`;
    }
    return '选择关联表单';
  };

  const getConfirmButtonText = () => {
    if (multiple) {
      return `确认选择（已选 ${selectedForms.length} 项）`;
    }
    return '确认选择';
  };

  const isConfirmDisabled = () => {
    if (multiple) {
      return selectedForms.length === 0;
    }
    return !selectedForm;
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      onOk={handleConfirm}
      width={800}
      okText={getConfirmButtonText()}
      cancelText="取消"
      okButtonProps={{ disabled: isConfirmDisabled() }}
      footer={[
        <div key="footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <Button onClick={handleClearAll} style={{ marginRight: 8 }}>
              清除所有
            </Button>
            <Button onClick={handleReset}>
              重置默认
            </Button>
          </div>
          <div>
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>
              取消
            </Button>
            <Button type="primary" onClick={handleConfirm} disabled={isConfirmDisabled()}>
              {getConfirmButtonText()}
            </Button>
          </div>
        </div>
      ]}
      style={{ top: 20 }}
    >
      {/* 主要内容区域 */}
      <div className="flex-1 flex overflow-hidden" style={{ minHeight: 0 }}>
        <div className="w-1/3 overflow-auto">
          <CategoryTreeSelector onChange={handleCategoryChange} />
        </div>
        <div className="w-2/3 overflow-hidden flex flex-col">
          <FormListSelector
            categoryFilter={categoryFilter}
            onSelect={handleFormSelect}
            selectedFormKey={selectedForm?.Key}
            multiple={multiple}
            selectedFormKeys={selectedForms.map(form => form.Id)}
            selectedForms={selectedForms}
          />
        </div>
      </div>
      
      {/* 选中表单显示区域 */}
      <div className="flex-shrink-0" style={{ marginTop: '12px' }}>
        {/* 单选模式显示 */}
        {!multiple && selectedForm && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded">
            <div className="text-sm">
              <strong>已选择表单：</strong>
              <span className="ml-2">{selectedForm.Title}</span>
              <span className="ml-4 text-gray-500">Key: {selectedForm.Id}</span>
            </div>
          </div>
        )}
        
        {/* 多选模式显示 */}
        {multiple && selectedForms.length > 0 && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded" style={{ maxHeight: '80px', overflowY: 'auto' }}>
            <div className="text-sm mb-2">
              <strong>已选择表单（{selectedForms.length}项）：</strong>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedForms.map(form => (
                <Tag 
                  key={form.Id} 
                  color="blue" 
                  closable
                  onClose={() => {
                    const newSelectedForms = selectedForms.filter(f => f.Id !== form.Id);
                    setSelectedForms(newSelectedForms);
                  }}
                >
                  {form.Title} ({form.Id})
                </Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FormSelector;